import { Outlet, createFileRoute } from "@tanstack/react-router";
import { Loader2, ShieldAlert } from "lucide-react";
import { useTRPC } from "@/integrations/trpc/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
	BuyerContext,
	useBuyerContextState,
	type BuyerData,
	type BuyerContextValue,
	type VendorProfile,
} from "@/contexts/vendor-buyer-context";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

// Re-export for convenience
export { useBuyerContext, useBuyerContextState, useVendorProfile } from "@/contexts/vendor-buyer-context";

export const Route = createFileRoute("/vendor/$buyerSlug")({
	// No beforeLoad data fetching - defer to component for client-side fetch
	// This avoids Cloudflare Workers SSR issues with self-referencing HTTP calls
	component: VendorBuyerLayout,
});

function VendorBuyerLayout() {
	const { buyerSlug } = Route.useParams();
	const trpc = useTRPC();

	// Check vendor authentication
	const { data: vendorStatus, isLoading: vendorLoading } = useQuery(
		trpc.vendorAuth.isVendor.queryOptions()
	);

	// Fetch buyer org info
	const { data: buyer, isLoading: buyerLoading, error } = useQuery(
		trpc.public.organizations.getBySlug.queryOptions({ slug: buyerSlug })
	);

	const isLoading = vendorLoading || buyerLoading;

	const buyerData: BuyerData | null = buyer ? {
		id: buyer.id,
		name: buyer.name,
		slug: buyerSlug,
	} : null;

	const contextValue: BuyerContextValue = {
		buyer: buyerData,
		vendorProfile: (vendorStatus?.profile as VendorProfile) ?? null,
		isLoading,
		error: error ?? null,
	};

	return (
		<BuyerContext.Provider value={contextValue}>
			<VendorBuyerLayoutContent
				vendorStatus={vendorStatus}
				vendorLoading={vendorLoading}
			/>
		</BuyerContext.Provider>
	);
}

interface VendorBuyerLayoutContentProps {
	vendorStatus: { isVendor: boolean; profile: VendorProfile | null } | undefined;
	vendorLoading: boolean;
}

function VendorBuyerLayoutContent({
	vendorStatus,
	vendorLoading,
}: VendorBuyerLayoutContentProps) {
	const { isLoading, error, buyer } = useBuyerContextState();
	const trpc = useTRPC();
	const hasRegisteredRef = useRef(false);

	// Register vendor engagement with this buyer
	const { mutate: registerEngagement } = useMutation(
		trpc.vendorPortal.engagement.register.mutationOptions({
			onSuccess: (data) => {
				console.log("[VendorPortal] Engagement registration result:", data);
			},
			onError: (error) => {
				console.error("[VendorPortal] Failed to register engagement:", error);
			},
		})
	);

	// Fire engagement registration when vendor is authenticated and buyer is loaded
	useEffect(() => {
		if (
			!hasRegisteredRef.current &&
			vendorStatus?.isVendor &&
			buyer?.id &&
			!isLoading &&
			!vendorLoading
		) {
			hasRegisteredRef.current = true;
			console.log("[VendorPortal] Registering engagement for buyer:", buyer.id);
			registerEngagement({ buyerOrgId: buyer.id });
		}
	}, [vendorStatus?.isVendor, buyer?.id, isLoading, vendorLoading, registerEngagement]);

	// Show loading state while checking authentication
	if (isLoading || vendorLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Check vendor authentication
	if (!vendorStatus?.isVendor) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
				<Card className="w-full max-w-md">
					<CardHeader className="space-y-1 text-center">
						<div className="mx-auto h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
							<ShieldAlert className="h-6 w-6 text-yellow-600" />
						</div>
						<CardTitle className="text-2xl font-bold">
							Vendor Access Required
						</CardTitle>
						<CardDescription>
							This area is only accessible to registered vendors. Please sign in
							through the main Bidscope platform to access the vendor portal.
						</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col items-center gap-4">
						<Button
							className="w-full"
							onClick={() =>
								(window.location.href = "https://app.bidscope.ai")
							}
						>
							Go to Bidscope
						</Button>
						<p className="text-sm text-muted-foreground text-center">
							Already have a Bidscope account? Sign in there and you'll be
							redirected here automatically.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Check buyer org exists
	if (error || !buyer) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
					<p className="text-muted-foreground">
						{error?.message || "The organization you're looking for doesn't exist or you don't have access."}
					</p>
				</div>
			</div>
		);
	}

	return <Outlet />;
}
